import React, { useEffect, useState } from 'react';
import { Paper, Typography, Backdrop, CircularProgress, Button, Modal, Box, Grid, TextField } from '@material-ui/core';
import TableComponent from '../TableComponent';
import PaginationComponent from '../PaginationComponent';
import LocationsService from '../../services/LocationsService';
import notify from '../../utils/notifier';

const jobColumns = [
  { id: 'id', label: 'Id', width: '18%' },
  { id: 'job_type', label: 'Job Type', width: '10%' },
  { id: 'status', label: 'Status', width: '8%' },
  { id: 'created_at', label: 'Created At', width: '16%' },
  { id: 'updated_at', label: 'Updated At', width: '16%' },
  { id: 'insertedCount', label: 'Inserted', width: '8%' },
  { id: 'updatedCount', label: 'Updated', width: '8%' },
  { id: 'errorCount', label: 'Errors', width: '8%' },
  { id: 'actions', label: 'Actions', width: '8%' },
];

const jobManageColumns = {
  id: true,
  job_type: true,
  status: true,
  created_at: true,
  updated_at: true,
  insertedCount: true,
  updatedCount: true,
  errorCount: true,
  actions: true,
};

const DEFAULT_ROWS_PER_PAGE = 10;

const JobTrackingPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [jobIdSearch, setJobIdSearch] = useState('');

  useEffect(() => {
    fetchJobs(page, rowsPerPage);
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const fetchJobs = (page, limit) => {
    setLoading(true);
    const fixedSort = { _sort: 'updatedAt', _order: 'desc' };
    LocationsService.getJobTrackingData({ _limit: limit, _page: page + 1, ...fixedSort })
      .then(resp => {
        if (Array.isArray(resp.data)) {
          setJobs(resp.data);
          setHasNextPage(resp.data.length === limit);
          if (resp.data.length === 0 && page > 0) setPage(page - 1);
        } else {
          setJobs([]);
          setHasNextPage(false);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleMenuClick = (event, row) => {
    setSelectedJob(row.full || row);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedJob(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const tableRows = jobs.map(j => ({
    id: j.id,
    job_type: j.job_type,
    status: j.status,
    created_at: new Date(j.created_at).toLocaleString(),
    updated_at: new Date(j.updated_at).toLocaleString(),
    insertedCount: j.response?.insertedCount != 0 ? j.response?.insertedCount : '0',
    updatedCount: j.response?.updatedCount != 0 ? j.response?.updatedCount : '0',
    errorCount: j.response?.errorCount != 0 ? j.response?.errorCount : '0',
    actions: 'View',
    full: j,
  }));

  // For MUI TablePagination: simulate count so the next button is conditionally visible
  const paginationCount = (page + 1) * rowsPerPage + (hasNextPage ? 1 : 0);

  const handleSearchInputChange = (e) => {
    setJobIdSearch(e.target.value);
  };

  const handleJobIdSearch = async (e) => {
    if (e) e.preventDefault();
    const jobId = jobIdSearch.trim();
    if (!jobId) return;
    setLoading(true);
    try {
      const resp = await LocationsService.getJobTrackingData({ _jobId: jobId });
      if (Array.isArray(resp.data) && resp.data.length === 1) {
        setSelectedJob(resp.data[0]);
        setViewOpen(true);
      } else {
        notify({ type: 'info', message: 'Job Id Not Found' });
      }
    } catch (e) {
      notify({ type: 'error', message: 'Error searching for job.' });
    } finally {
      setLoading(false);
      setJobIdSearch('');
    }
  };

  // On clear of search, just reset search field
  const handleSearchClear = () => {
    setJobIdSearch('');
  };

  // Helper: render errorData nicely
  function renderErrorData(errorData) {
    if (!errorData || errorData.length === 0) return null;
    if (typeof errorData[0] === 'object') {
      // Table case: array of error objects
      return (
        <Box style={{ marginBottom: 18 }}>
          <Typography variant="subtitle1"><b>Errors:</b></Typography>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, marginBottom: 8 }}>
            <thead>
              <tr style={{ background: '#ececec' }}>
                <th style={{ padding: 4, borderBottom: '1px solid #ddd', textAlign: 'left', width: 100 }}>Row</th>
                <th style={{ padding: 4, borderBottom: '1px solid #ddd', textAlign: 'left' }}>Error</th>
              </tr>
            </thead>
            <tbody>
              {errorData.map((err, idx) => (
                <tr key={idx}>
                  <td style={{ padding: 4 }}>{err.row_number || '-'}</td>
                  <td style={{ padding: 4 }}>{err.error_message || String(err)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    } else {
      // List case: array of error strings
      return (
        <div style={{ marginBottom: 18 }}>
          <Typography variant="subtitle1"><b>Errors:</b></Typography>
          <ul>
            {errorData.map((err, idx) => (
                <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      );
    }
  }

  return (
    <div style={{ backgroundColor: '#F2F2F2', padding: '20px 40px' }}>
      <Typography variant="h5" gutterBottom><b>Job Tracking</b></Typography>
      {/* Search Row */}
      <form onSubmit={handleJobIdSearch} style={{ marginBottom: 24 }}>
        <Grid container spacing={2} alignItems="center" style={{maxWidth:550}}>
          <Grid item xs={8}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search Job ID"
              value={jobIdSearch}
              onChange={handleSearchInputChange}
              inputProps={{ style: { fontWeight: 500, letterSpacing: 0.2 } }}
            />
          </Grid>
          <Grid item xs={4}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              style={{ height: 40, fontWeight: 600 }}
              disabled={!jobIdSearch.trim()}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </form>
      <Paper style={{ padding: 20, backgroundColor: '#fff' }}>
        <Backdrop open={loading} style={{ zIndex: 10000, color: '#3853BC' }}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <TableComponent
          rows={tableRows}
          columns={jobColumns}
          manageColumns={jobManageColumns}
          handleMenuClick={handleMenuClick}
          circularBackdrop={loading}
        />
        <PaginationComponent
          queryCount={paginationCount}
          page={page}
          handleChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
      <Modal open={viewOpen} onClose={handleViewClose}>
        <Box style={{ position: 'absolute', top: 50, left: '10%', width: '80%', background: '#fff', padding: 30, maxHeight: '90vh', overflowY: 'auto' }}>
          {selectedJob && (
            <div>
              <Typography variant="h6">Job Detail</Typography>
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6}><b>Id:</b> {selectedJob.id}</Grid>
                <Grid item xs={12} sm={6}><b>Status:</b> {selectedJob.status}</Grid>
                <Grid item xs={12} sm={6}><b>Job Type:</b> {selectedJob.job_type}</Grid>
                <Grid item xs={12} sm={6}><b>Created:</b> {new Date(selectedJob.created_at).toLocaleString()}</Grid>
                <Grid item xs={12} sm={6}><b>Updated:</b> {new Date(selectedJob.updated_at).toLocaleString()}</Grid>
                <Grid item xs={12} sm={6}><b>Inserted Count:</b> {selectedJob.response?.insertedCount != 0 ? selectedJob.response?.insertedCount : '0'}</Grid>
                <Grid item xs={12} sm={6}><b>Updated Count:</b> {selectedJob.response?.updatedCount != 0 ? selectedJob.response?.updatedCount : '0'}</Grid>
                <Grid item xs={12} sm={6}><b>Error Count:</b> {selectedJob.response?.errorCount != 0 ? selectedJob.response?.errorCount : '0'}</Grid>
              </Grid>
              {selectedJob.metadata?.errorData && renderErrorData(selectedJob.metadata.errorData)}
              <Typography variant="subtitle1"><b>Raw Data:</b></Typography>
              <pre style={{ fontSize: 13, whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: 14, borderRadius: 6 }}>{JSON.stringify(selectedJob, null, 2)}</pre>
              <Button onClick={handleViewClose} color="primary" variant="contained" style={{ marginTop: 12 }}>Close</Button>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default JobTrackingPage;
